import type { EventStatus } from '../../../schema/sport/entry.js';
import type { EventStatusInDraw } from './utils.js';

const getPeriodText = (sportEventStatus: EventStatus): string => {
  if (sportEventStatus === 'FirstPeriod') {
    return ' period 1, i';
  }
  if (sportEventStatus === 'SecondPeriod') {
    return ' period 2, i';
  }
  if (sportEventStatus === 'ThirdPeriod') {
    return ' period 3, i';
  }
  return '';
};

const statusWithPeriodsAndHalftime = new Set(['FirstHalf', 'SecondHalf', 'FirstPeriod', 'SecondPeriod', 'ThirdPeriod']);

type GetLabelForEventProps = {
  eventResult: string | undefined;
  matchStatusText: string;
  sportEventStatus: EventStatus;
  matchDrawStatus?: EventStatusInDraw;
};

/**
 * Generates an accessible label for a sport event based on its status and result.
 *
 * @param root0 props for getLabelForSportEvent
 * @param root0.eventResult The result of the event, if available
 * @param root0.matchStatusText The current status of the match
 * @param root0.sportEventStatus The specific status of the sport event
 * @param root0.matchDrawStatus The status of the current event
 * @returns A string label describing the event status and result
 */
export function getLabelForEvent({
  eventResult,
  matchStatusText,
  sportEventStatus,
  matchDrawStatus,
}: GetLabelForEventProps): string {
  if (!matchStatusText) {
    return '';
  }

  const isOngoing = matchDrawStatus === 'Ongoing';
  const isFinished = matchDrawStatus === 'Finished';
  const isCancelled = matchDrawStatus === 'Cancelled';
  const hasEventStarted = isOngoing || isFinished;

  function getMatchLabel(): string | undefined {
    if (sportEventStatus === 'Halftime') {
      return 'Match startad, halvtid spelad';
    }
    if (sportEventStatus === 'Cancelled') {
      return 'Match inställd';
    }
    if (sportEventStatus === 'Delayed') {
      return 'Match försenad';
    }
    if (isOngoing && statusWithPeriodsAndHalftime.has(sportEventStatus)) {
      return `Match pågår i${getPeriodText(sportEventStatus)} minut ${matchStatusText}`;
    }
    if (isOngoing) {
      // General ongoing match for other sports without specific half/periods
      return 'Match pågår';
    }
    if (isFinished) {
      return `Match avslutad`;
    }
    if (!isCancelled && sportEventStatus === 'NotStarted') {
      return `Match startar ${matchStatusText}`;
    }
    return undefined;
  }

  const getResultText = (): string => {
    if (!hasEventStarted) {
      return '';
    }

    let formattedResult = eventResult ? `, resultat ${eventResult}` : '';
    formattedResult = isCancelled ? '' : formattedResult;

    return formattedResult;
  };

  return `${getMatchLabel()}${getResultText()}`;
}
